import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant, Button } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import { Contract } from '@ethersproject/contracts'
import type { MarketplaceOffer } from '@mprolab/sdk'
import MainWrapper from 'layouts/MainWrapper'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { readOffer, isExpired } from 'utils/marketplace/readOffers'
import { useMarketplaceContext } from 'utils/marketplace/useMarketplaceContext'
import { useWeb3ProviderAndContract } from 'hooks/useWeb3ProviderAndContract'
import { ERC721_MIN_ABI, MARKETPLACE_ABI } from 'utils/marketplace/abi'
import { decodeMarketplaceError } from 'utils/marketplace/errorMessages'
import { readOwnedLicenceTokenIds } from 'utils/marketplace/readOwnedLicences'
import { useUnclaimedForToken } from 'utils/marketplace/useUnclaimedForToken'
import { reflectLicenceTransfer } from 'utils/marketplace/reflectLicenceTransfer'
import { invalidateLicenceLists } from 'state/services/indexNode/indexNode'
import { useLicenceNamesByToken } from 'hooks/useLicenceNamesByToken'
import TypeBadge from './components/TypeBadge'
import ExpiryNote from './components/ExpiryNote'
import TxConfirmation from 'components/TxConfirmation'
import useToast from 'hooks/useToast'
import { useSwitchChainForContractTx } from 'hooks/useSwitchChainForContractTx'

const Section = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
`

const Select = styled.select`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

/**
 * A standing buy offer, and the place a seller sells into it. The seller
 * picks WHICH of their tokens goes — and sees the platform-verified
 * unclaimed value of that token first, because here it protects the
 * SELLER's pricing: accepting with a loaded licence gives away accrued MPRO
 * the bid never priced.
 */
const OfferDetailView: React.FC = () => {
  const { offerId } = useParams<{ offerId: string }>()
  const { t } = useTranslation()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { account, provider } = useWeb3React()
  const ctx = useMarketplaceContext()
  const ensureChainForTx = useSwitchChainForContractTx()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  const { toastSuccess, toastError } = useToast()
  const licenceNames = useLicenceNamesByToken()

  const [offer, setOffer] = useState<MarketplaceOffer | null>(null)
  const [loading, setLoading] = useState(true)
  const [ownedTokenIds, setOwnedTokenIds] = useState<string[] | null>(null)
  const [tokenId, setTokenId] = useState('')
  const [hasApproval, setHasApproval] = useState<boolean | null>(null)
  const [busy, setBusy] = useState<'approve' | 'accept' | 'cancel' | null>(null)
  const [txHash, setTxHash] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    if (!offerId || !ctx.marketplaceAddress) return
    setLoading(true)
    try {
      const rpc = getSimpleRpcProviderByChainId(ctx.chainId)
      setOffer(
        await readOffer({
          marketplaceAddress: ctx.marketplaceAddress,
          provider: rpc as ethers.providers.Provider,
          offerId
        })
      )
    } finally {
      setLoading(false)
    }
  }, [offerId, ctx.marketplaceAddress, ctx.chainId])

  useEffect(() => {
    void refetch()
  }, [refetch])

  // The seller's candidate tokens: what this wallet holds in the offer's
  // collection, read from the collection itself.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!account || !offer) {
        setOwnedTokenIds(null)
        return
      }
      try {
        const rpc = getSimpleRpcProviderByChainId(ctx.chainId)
        const ids = await readOwnedLicenceTokenIds({
          nft: offer.nft,
          owner: account,
          provider: rpc as ethers.providers.Provider
        })
        if (!cancelled) {
          setOwnedTokenIds(ids)
          setTokenId((current) => (current && ids.includes(current) ? current : ids.length === 1 ? ids[0] : ''))
        }
      } catch {
        if (!cancelled) setOwnedTokenIds([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [account, offer, ctx.chainId])

  // NFT approval state for the accept — the same requirement listing has.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!account || !offer || !ctx.marketplaceAddress || !tokenId) {
        setHasApproval(null)
        return
      }
      try {
        const rpc = getSimpleRpcProviderByChainId(ctx.chainId)
        const erc721 = new Contract(
          offer.nft,
          ERC721_MIN_ABI as unknown as ethers.ContractInterface,
          rpc as ethers.providers.Provider
        )
        const [perToken, forAll] = await Promise.all([
          erc721.getApproved(tokenId).catch(() => ethers.constants.AddressZero),
          erc721.isApprovedForAll(account, ctx.marketplaceAddress).catch(() => false)
        ])
        if (!cancelled) {
          setHasApproval(Boolean(forAll) || String(perToken).toLowerCase() === ctx.marketplaceAddress.toLowerCase())
        }
      } catch {
        if (!cancelled) setHasApproval(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [account, offer, tokenId, ctx.marketplaceAddress, ctx.chainId])

  // What the chosen token carries — the seller's own money on the table.
  const unclaimed = useUnclaimedForToken(offer?.nft, tokenId || undefined)

  const unitMpro = useMemo(() => {
    if (!offer) return '0'
    try {
      return ethers.utils.formatEther(offer.unitPriceWei)
    } catch {
      return '?'
    }
  }, [offer])

  const collectionLabel = useMemo(
    () =>
      ctx.collections.find((c) => c.nft === offer?.nft)?.label ??
      (offer ? `${offer.nft.slice(0, 6)}…${offer.nft.slice(-4)}` : ''),
    [ctx.collections, offer]
  )

  const isMine = Boolean(account && offer && offer.buyer === account.toLowerCase())
  const expired = isExpired(offer?.expiresAt)

  const onApprove = useCallback(async () => {
    if (!provider || !account || !offer || !ctx.marketplaceAddress) return
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('approve')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const erc721 = new Contract(offer.nft, ERC721_MIN_ABI as unknown as ethers.ContractInterface, signer)
      const tx = await erc721.setApprovalForAll(ctx.marketplaceAddress, true)
      await tx.wait()
      setHasApproval(true)
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
    } finally {
      setBusy(null)
    }
  }, [
    provider,
    account,
    offer,
    ctx.marketplaceAddress,
    ctx.chainId,
    ensureChainForTx,
    getProviderOrSignerByChainId,
    toastError,
    t
  ])

  const onAccept = useCallback(async () => {
    if (!provider || !account || !offer || !ctx.marketplaceAddress || !tokenId) return
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('accept')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const market = new Contract(
        ctx.marketplaceAddress,
        MARKETPLACE_ABI as unknown as ethers.ContractInterface,
        signer
      )
      // minUnitPrice = the price on screen: if the buyer lowers the offer in
      // front of this accept, the contract refuses instead of underpaying.
      const tx = await market.accept(offer.offerId, tokenId, offer.unitPriceWei)
      setTxHash(tx.hash)
      await tx.wait()
      // The licence changed hands: reflect it, then drop every cached
      // licence list so both parties' next fetch is already true.
      await reflectLicenceTransfer(offer.nft, tokenId)
      dispatch(invalidateLicenceLists())
      toastSuccess(t('marketplace.acceptOk', { amount: unitMpro }))
      await refetch()
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
      setTxHash(null)
    } finally {
      setBusy(null)
    }
  }, [
    provider,
    account,
    offer,
    tokenId,
    ctx.marketplaceAddress,
    ctx.chainId,
    ensureChainForTx,
    getProviderOrSignerByChainId,
    dispatch,
    refetch,
    toastSuccess,
    toastError,
    unitMpro,
    t
  ])

  const onCancel = useCallback(async () => {
    if (!provider || !account || !offer || !ctx.marketplaceAddress) return
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('cancel')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const market = new Contract(
        ctx.marketplaceAddress,
        MARKETPLACE_ABI as unknown as ethers.ContractInterface,
        signer
      )
      const tx = await market.cancelOffer(offer.offerId)
      setTxHash(tx.hash)
      await tx.wait()
      await refetch()
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
    } finally {
      setBusy(null)
    }
  }, [
    provider,
    account,
    offer,
    ctx.marketplaceAddress,
    ctx.chainId,
    ensureChainForTx,
    getProviderOrSignerByChainId,
    refetch,
    toastError,
    t
  ])

  if (loading || !offer) {
    return (
      <MainWrapper>
        <Flex fullWidth justifyContent="center" mt="200px" mb="200px">
          <Text color={colors.gray6}>{loading ? t('marketplace.loading') : t('marketplace.offerNotFound')}</Text>
        </Flex>
      </MainWrapper>
    )
  }

  return (
    <MainWrapper>
      <Flex fullWidth flexDirection="column" mt="152px" mb="120px" gap="16px">
        <Flex alignItems="center" gap="12px">
          <TypeBadge wanted />
          <Text variant={TextVariant.H4}>
            {t('marketplace.wantedTitle', {
              count: Number(offer.remaining),
              label: collectionLabel
            })}
          </Text>
        </Flex>

        <Section>
          <Flex justifyContent="space-between">
            <Text color={colors.gray6}>{t('marketplace.pricePerToken')}</Text>
            <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.purple}>
              {unitMpro} MPRO
            </Text>
          </Flex>
          <Flex justifyContent="space-between">
            <Text color={colors.gray6}>{t('marketplace.offerRemaining')}</Text>
            <Text>{offer.remaining}</Text>
          </Flex>
          <Flex justifyContent="space-between">
            <Text color={colors.gray6}>{t('marketplace.offerBuyer')}</Text>
            <Text>{`${offer.buyer.slice(0, 8)}…${offer.buyer.slice(-6)}`}</Text>
          </Flex>
          <ExpiryNote expiresAt={offer.expiresAt} />
          {!offer.active && <Text color={colors.gray6}>{t('marketplace.offerInactive')}</Text>}
        </Section>

        {txHash && (
          <Section>
            <TxConfirmation hash={txHash} chainId={ctx.chainId} title={t('marketplace.acceptedTitle')} />
          </Section>
        )}

        {isMine && offer.active && (
          <Section>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('marketplace.yourOffer')}
            </Text>
            <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => void onCancel()}>
              {busy === 'cancel' ? t('marketplace.cancelling') : t('marketplace.cancelOffer')}
            </Button>
          </Section>
        )}

        {!isMine && offer.active && !expired && account && (
          <Section>
            <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('marketplace.sellIntoOffer')}</Text>
            {ownedTokenIds === null ? (
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {t('marketplace.loadingLicences')}
              </Text>
            ) : ownedTokenIds.length === 0 ? (
              <Text color={colors.gray6}>{t('marketplace.noLicencesHere')}</Text>
            ) : (
              <>
                <Select value={tokenId} onChange={(e) => setTokenId(e.target.value)}>
                  <option value="">{t('marketplace.chooseLicencePlaceholder')}</option>
                  {ownedTokenIds.map((id) => (
                    <option key={id} value={id}>
                      {licenceNames[id]
                        ? `${licenceNames[id]} · ${collectionLabel} #${id}`
                        : `${collectionLabel} #${id}`}
                    </option>
                  ))}
                </Select>

                {/*
                  The seller's own money on the table: what this token carries
                  goes WITH it, and the bid never priced it. Stated by the
                  platform, before the wallet opens.
                */}
                {tokenId && unclaimed.unclaimedMpro !== null && unclaimed.unclaimedMpro > 0 && (
                  <Text variant={TextVariant.BODY_SMALL} color={colors.yellow ?? colors.cyan}>
                    {t('marketplace.acceptCarriesUnclaimed', {
                      amount: unclaimed.unclaimedMpro.toLocaleString('en-US', {
                        maximumFractionDigits: 2
                      })
                    })}
                  </Text>
                )}

                {tokenId && hasApproval === false ? (
                  <Button disabled={busy !== null} onClick={() => void onApprove()}>
                    {busy === 'approve' ? t('marketplace.approving') : t('marketplace.approveCollection')}
                  </Button>
                ) : (
                  <Button disabled={busy !== null || !tokenId || hasApproval !== true} onClick={() => void onAccept()}>
                    {busy === 'accept'
                      ? t('marketplace.accepting')
                      : t('marketplace.acceptOffer', { amount: unitMpro })}
                  </Button>
                )}
              </>
            )}
          </Section>
        )}
      </Flex>
    </MainWrapper>
  )
}

export default OfferDetailView
